import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMG = {
  hero: "/products/the-standard.png",
  story: "/products/the-signature.png",
  look1: "/products/the-crest.png",
  look2: "/products/hoodie.png",
  look3: "/products/the-meridian.png",
};

const collections = [
  { num: "001", name: "Monochrome", status: "Coming Soon", desc: "The debut collection — tailored essentials in black, white, and charcoal." },
  { num: "002", name: "The Essentials", status: "In Development", desc: "Elevated daily pieces built on premium materials and precise construction." },
  { num: "003", name: "Tailored Series", status: "Concept", desc: "Structured outerwear and formal silhouettes. Currently in design." },
];

const lookbook = [
  { img: IMG.look1, caption: "No. 01 — Structure", offset: "" },
  { img: IMG.look2, caption: "No. 02 — Monochrome", offset: "md:mt-16" },
  { img: IMG.look3, caption: "No. 03 — Silhouette", offset: "md:mt-32" },
];

const products = [
  { name: "The Standard", detail: "Heavyweight Tee" },
  { name: "The Signature", detail: "Tailored Overshirt" },
  { name: "The Meridian", detail: "Pleated Trouser" },
  { name: "The Crest", detail: "Structured Outerwear" },
];

const Clothing = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "ADD VALUE Clothing — Luxury Apparel";
  }, []);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/waitlist`, { email });
      if (res.data.status === "exists") {
        toast.info("You're already on the list.");
      } else {
        toast.success("You're on the list. We'll be in touch.");
      }
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="clothing-page">
      <PageHero
        testid="clothing-hero"
        image={IMG.hero}
        overline="ADD VALUE Clothing — A Division of ADD VALUE HOLDINGS LLC"
        lines={["Adding value through", "what you wear."]}
        lede="A luxury apparel division built on restraint, precision, and material quality — designed in black and white, made to endure."
      />

      {/* BRAND STORY */}
      <section className="border-t border-[#1c1c1f]">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 sm:px-8 lg:grid-cols-12 lg:px-12 lg:py-32">
          <Reveal className="lg:col-span-5">
            <Overline className="!text-stone-300 [&>span]:!bg-stone-400/70">The Brand Story</Overline>
            <h2 className="mt-8 font-serif text-2xl leading-snug tracking-tight text-white/95 sm:text-3xl lg:text-4xl">
              Clothing with the same standard as the name on it.
            </h2>
            <p className="mt-8 text-sm font-light leading-relaxed text-stone-400 sm:text-base">
              ADD VALUE Clothing is the first consumer brand of ADD VALUE
              HOLDINGS LLC. It is not streetwear and it is not fast fashion —
              it is a study in restraint: tailored silhouettes, monochrome
              palettes, and materials chosen to age well.
            </p>
            <p className="mt-6 text-sm font-light leading-relaxed text-stone-400 sm:text-base">
              Every piece is being developed with intention. Nothing carries
              the name until it earns it.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="overflow-hidden border border-[#222226]">
              <img
                src={IMG.story}
                alt="ADD VALUE Clothing — The Signature tailored overshirt"
                loading="lazy"
                className="h-full max-h-[540px] w-full object-cover grayscale transition-transform duration-700 ease-out hover:scale-105"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="border-t border-[#1c1c1f] bg-[#0d0d0f]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="mb-14">
            <Overline className="!text-stone-300 [&>span]:!bg-stone-400/70">Collections</Overline>
            <h2 className="mt-6 font-serif text-2xl tracking-tight text-white/95 sm:text-3xl lg:text-4xl">
              Three chapters, in development.
            </h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {collections.map((c, i) => (
              <Reveal key={c.num} delay={0.08 * i}>
                <div
                  data-testid={`collection-${c.num}`}
                  className="group flex h-full flex-col border border-[#222226] bg-[#0A0A0B] p-8 transition-colors duration-500 hover:border-stone-400/50 lg:p-10"
                >
                  <div className="flex items-center justify-between">
                    <p className="font-serif text-3xl italic text-stone-400">
                      {c.num}
                    </p>
                    <span className="border border-[#2a2a2e] px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-stone-400">
                      {c.status}
                    </span>
                  </div>
                  <h3 className="mt-8 font-serif text-2xl text-white">{c.name}</h3>
                  <p className="mt-4 text-sm font-light leading-relaxed text-stone-500">
                    {c.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LOOKBOOK */}
      <section className="border-t border-[#1c1c1f]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <div>
              <Overline className="!text-stone-300 [&>span]:!bg-stone-400/70">Lookbook</Overline>
              <h2 className="mt-6 font-serif text-2xl tracking-tight text-white/95 sm:text-3xl lg:text-4xl">
                A study in black & white.
              </h2>
            </div>
            <p className="max-w-sm text-sm font-light text-stone-500">
              Editorial direction for the world of ADD VALUE Clothing.
            </p>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {lookbook.map((l, i) => (
              <Reveal key={i} delay={0.08 * i} className={l.offset}>
                <figure
                  data-testid={`lookbook-item-${i + 1}`}
                  className="group relative overflow-hidden border border-[#222226]"
                >
                  <img
                    src={l.img}
                    alt={l.caption}
                    loading="lazy"
                    className="aspect-[3/4] w-full object-cover grayscale transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A0A0B] to-transparent p-6 pt-16 font-mono text-[10px] uppercase tracking-[0.3em] text-stone-300">
                    {l.caption}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="border-t border-[#1c1c1f] bg-[#0d0d0f]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="mb-14">
            <Overline className="!text-stone-300 [&>span]:!bg-stone-400/70">Featured Products</Overline>
            <h2 className="mt-6 font-serif text-2xl tracking-tight text-white/95 sm:text-3xl lg:text-4xl">
              The first pieces. Coming soon.
            </h2>
          </Reveal>
          <div className="grid gap-px border border-[#222226] bg-[#222226] sm:grid-cols-2 lg:grid-cols-4">
            {products.map((p, i) => (
              <Reveal key={p.name} delay={0.06 * i} className="bg-[#0d0d0f]">
                <div
                  data-testid={`product-${i + 1}`}
                  className="flex h-full flex-col p-8 transition-colors duration-500 hover:bg-[#121214]"
                >
                  <span className="self-start border border-stone-500/50 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-stone-300">
                    Coming Soon
                  </span>
                  <h3 className="mt-24 font-serif text-2xl text-white">{p.name}</h3>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500">
                    {p.detail}
                  </p>
                  <p className="mt-auto pt-8 font-mono text-xs text-stone-600">
                    Price on release
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WAITLIST / FUTURE STORE */}
      <section className="border-t border-[#1c1c1f]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="max-w-2xl">
            <Overline className="!text-stone-300 [&>span]:!bg-stone-400/70">The Store</Overline>
            <span
              data-testid="shop-coming-soon-badge"
              className="mt-8 inline-block border border-stone-500/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-stone-300"
            >
              Shop — Coming Soon
            </span>
            <h2 className="mt-6 font-serif text-2xl leading-snug tracking-tight text-white/95 sm:text-3xl lg:text-4xl">
              The online store is under development.
            </h2>
            <p className="mt-6 text-sm font-light leading-relaxed text-stone-400 sm:text-base">
              Be first to know when Collection 001 becomes available. Leave
              your email — no noise, only the release.
            </p>
            <form
              data-testid="clothing-waitlist-form"
              onSubmit={joinWaitlist}
              className="mt-10 flex flex-col gap-4 sm:flex-row"
            >
              <input
                data-testid="clothing-waitlist-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="w-full border border-[#2a2a2e] bg-[#121214] px-5 py-4 text-sm text-white placeholder:text-stone-600 focus:border-stone-400 focus:outline-none"
              />
              <button
                data-testid="clothing-waitlist-submit"
                type="submit"
                disabled={submitting}
                className="group inline-flex shrink-0 items-center justify-center gap-3 bg-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-stone-300 disabled:opacity-50"
              >
                {submitting ? "Joining…" : "Notify Me"}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Clothing;
